/** Repères des modes d'action d'un ENI personnalisé (`[niveauInitialEni]`, `[eni111?.localityT1]`…), remplacés dans
 *  chaque dossier par les noms de la carte. Hors dossier (préparation de l'ENI), ils sont montrés en clair entre
 *  chevrons (« ⟨premier échelon ennemi⟩ ») et reconvertis à l'enregistrement : la liste fermée du serveur
 *  (`custom_me.MARKERS`), libellés uniques dans chaque langue. */

const LABELS: Record<string, readonly [fr: string, en: string]> = {
  "[niveauInitialEni]": ["premier échelon ennemi", "enemy first echelon"],
  "[niveauInitialEniLocation]": ["position du premier échelon", "first echelon position"],
  "[niveauUltérieurEni]": ["échelon ultérieur ennemi", "enemy follow-on echelon"],
  "[niveauUltérieurEniLocation]": ["position de l'échelon ultérieur", "follow-on echelon position"],
  "[appuisEni]": ["appuis ennemis", "enemy supports"],
  "[appuisEniLocation]": ["position des appuis", "supports position"],
  "[renfortsEni]": ["renforts ennemis", "enemy reinforcements"],
  "[renfortsEniLocation]": ["position des renforts", "reinforcements position"],
  "[niveauGlobalEni]": ["force ennemie", "enemy force"],
  "[niveauGlobalEni.nom]": ["la force ennemie", "the enemy force"],
  "[niveauGlobalEni.Nom]": ["La force ennemie", "The enemy force"],
  "[niveauGlobalEni.de]": ["de la force ennemie", "of the enemy force"],
  "[niveauGlobalEni.a]": ["à la force ennemie", "to the enemy force"],
  "[niveauGlobalEni.bare]": ["nom de la force ennemie", "enemy force name"],
  "[eni111]": ["1re unité ennemie", "1st enemy unit"],
  "[eni112]": ["2e unité ennemie", "2nd enemy unit"],
  "[eni113]": ["3e unité ennemie", "3rd enemy unit"],
  "[eni111.de]": ["de la 1re unité ennemie", "of the 1st enemy unit"],
  "[eni112.de]": ["de la 2e unité ennemie", "of the 2nd enemy unit"],
  "[eni113.de]": ["de la 3e unité ennemie", "of the 3rd enemy unit"],
  "[eni111.nom]": ["la 1re unité ennemie", "the 1st enemy unit"],
  "[eni112.nom]": ["la 2e unité ennemie", "the 2nd enemy unit"],
  "[eni113.nom]": ["la 3e unité ennemie", "the 3rd enemy unit"],
  "[eni111+112.de]": ["des 1re et 2e unités ennemies", "of the 1st and 2nd enemy units"],
  "[eni111+113.de]": ["des 1re et 3e unités ennemies", "of the 1st and 3rd enemy units"],
  "[eni112+113.de]": ["des 2e et 3e unités ennemies", "of the 2nd and 3rd enemy units"],
  "[eni111+112+113.de]": ["des trois unités ennemies", "of the three enemy units"],
  "[eni111+112.nom]": ["les 1re et 2e unités ennemies", "the 1st and 2nd enemy units"],
  "[eni111+113.nom]": ["les 1re et 3e unités ennemies", "the 1st and 3rd enemy units"],
  "[eni112+113.nom]": ["les 2e et 3e unités ennemies", "the 2nd and 3rd enemy units"],
  "[eni111+112+113.nom]": ["les trois unités ennemies", "the three enemy units"],
  "[eni111?.localityT1]": ["localité de la 1re unité ennemie en T1", "1st enemy unit's locality in T1"],
  "[eni112?.localityT1]": ["localité de la 2e unité ennemie en T1", "2nd enemy unit's locality in T1"],
  "[eni113?.localityT1]": ["localité de la 3e unité ennemie en T1", "3rd enemy unit's locality in T1"],
  "[neighboringLocality.first]": ["première localité voisine", "first neighbouring locality"],
  "[neighboringLocality.last]": ["dernière localité voisine", "last neighbouring locality"],
};

const OPEN = "⟨";
const CLOSE = "⟩";
const MARKER_RE = /\[[^[\]]{1,60}\]/g;
const READABLE_RE = /⟨([^⟨⟩]{1,80})⟩/g;

function index(language: "fr" | "en"): number {
  return language === "en" ? 1 : 0;
}

/** Texte d'un mode d'action montré en clair : chaque repère connu devient « ⟨libellé⟩ » (les autres restent). */
export function readableMarkers(text: string, language: "fr" | "en"): string {
  const i = index(language);
  return text.replace(MARKER_RE, (m) => {
    const label = LABELS[m];
    return label ? `${OPEN}${label[i]}${CLOSE}` : m;
  });
}

/** Inverse de `readableMarkers` : « ⟨libellé⟩ » redevient son repère ; un libellé retouché reste tel quel. */
export function markersFromReadable(text: string, language: "fr" | "en"): string {
  const i = index(language);
  const reverse = new Map(Object.entries(LABELS).map(([marker, label]) => [label[i], marker]));
  return text.replace(READABLE_RE, (whole, label: string) => reverse.get(label) ?? whole);
}

/** Le texte contient-il des repères (pour n'afficher l'explication que lorsqu'elle sert) ? */
export function hasMarkers(text: string): boolean {
  return Object.keys(LABELS).some((m) => text.includes(m));
}

export const MARKER_LIST = Object.keys(LABELS);
